import { Component, Input, ViewEncapsulation } from '@angular/core';
import { CdftXmtWhvTest, ValveType } from 'app/modules/cdft/model';
import { StringEquipmentState } from 'app/modules/cdft/state/string-equipment.state';
import { ValveOptionTypeState } from 'app/modules/cdft/state/valve-option-type.state';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { first, isNull } from 'lodash';
import module from 'app/modules/cdft/cdft.module';

@Component({
    selector: 'app-greasing-pressure-table',
    templateUrl: './greasing-pressure-table.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./greasing-pressure-table.component.scss'],
})
export class GreasingPressureTableComponent {
    public minutes: any = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    @Input() formGroup: any;
    @Input() valveType: ValveType;
    observable$ = {
        stringEquipment: null,
        valveType: null,
        cdftState: null,
        results: [],
    };

    constructor(
        private stringEquipmentState: StringEquipmentState,
        private valveOptionTypeState: ValveOptionTypeState,
        private state: CDFTState
    ) { }

    get isAnnulus() {
        return this.valveType.includes('annulus');
    }

    get input() {
        return this.stringEquipmentState[this.valveType] ?? null;
    }

    get beforeResult() {
        return this.formGroup
            .get('cdftXmtWhvGreasing')
            .get('before')
            .value.result.toLowerCase();
    }

    get afterResult() {
        return this.formGroup
            .get('cdftXmtWhvGreasing')
            .get('after')
            .value.result.toLowerCase();
    }

    ngOnInit() {
        this.onStringEquipment();
        this.onValveType();
        this.onValveTest();
        this.onGreasing();
        this.onPopulate();
        this.onJammed()
        this.onFinding();
    }

    ngOnDestroy() {
        for (const key in this.observable$) {
            if (key == 'results') {
                this.observable$.results.forEach((result) => {
                    result?.unsubscribe();
                });
                continue;
            }
            this.observable$[key]?.unsubscribe();
        }
    }

    onPopulate() {
        this.observable$.cdftState = this.state.subscribe((cdft: CDFTState) => {
            if (!cdft.cdftXmtWhvTest) return;
            const cdftXmtWhvTests = cdft.cdftXmtWhvTest.filter(
                (cdftXmtWhvTest) => {
                    return (
                        cdftXmtWhvTest.mdSceValveTypeId ==
                        this.valveOptionTypeState.getItem(this.valveType).id
                    );
                }
            );

            if (!cdftXmtWhvTests.length) return;

            this.formGroup.patchValue(first(cdftXmtWhvTests));

            const data: CdftXmtWhvTest = first(cdftXmtWhvTests);

            const after = first(
                data.cdftXmtWhvGreasing.filter((cdftXmtWhvGreasing) => {
                    return cdftXmtWhvGreasing.type == 'after';
                })
            );
            const before = first(
                data.cdftXmtWhvGreasing.filter((cdftXmtWhvGreasing) => {
                    return cdftXmtWhvGreasing.type == 'before';
                })
            );
            this.formGroup
                .get('cdftXmtWhvGreasing')
                .patchValue({ after, before });

            // check if time greasing is null for before and after (applicable for Yes valve test)
            const valveTest = this.formGroup.get('valvedTest').value
            if (valveTest === 'Yes') {
                const greasingGroup = this.formGroup.get('cdftXmtWhvGreasing');
                const after = greasingGroup?.get('after')?.value;
                const before = greasingGroup?.get('before')?.value;

                const isEmpty = (obj: any) => obj?.time0 == null && obj?.time10 == null;

                if (isEmpty(after)) {
                    greasingGroup?.get('after')?.patchValue({ ...after, result: 'UTT' });
                }

                if (isEmpty(before)) {
                    greasingGroup?.get('before')?.patchValue({ ...before, result: 'UTT' });
                }
            }
        });
    }

    onStringEquipment() {
        this.observable$.stringEquipment = this.stringEquipmentState.subscribe(
            (stringEquipment: StringEquipmentState) => {
                const input = this.isAnnulus
                    ? stringEquipment.toAnnulus(this.valveType)
                    : stringEquipment.toSceValve(this.valveType);
                this.formGroup.patchValue(input);

                this.onActuated(input.actuatorType)
            }
        );
    }

    onValveType() {
        this.observable$.valveType = this.valveOptionTypeState.subscribe(
            (valveOptionType: ValveOptionTypeState) => {
                if (!valveOptionType.items.length) return;

                this.formGroup.patchValue({
                    mdSceValveTypeId: valveOptionType.getItem(this.valveType)
                        .id,
                });
            }
        );
    }

    onValveTest() {
        const valveBehaviour = (valvedTest) => {
            const isDisabled = valvedTest == 'NA';

            // Disabled if UTT
            [
                'finding',
                'greasing',
                'greasingType',
                'flushing',
                'flushingType',
                'sealing',
                'sealingType',
            ].forEach((element) => {
                if (isDisabled) {
                    this.formGroup.get(element).disable();
                } else {
                    this.formGroup.get(element).enable();
                }
            });

            // Enable if UTT
            ['remarks'].forEach((element) => {
                if (valvedTest == 'UTT' || valvedTest == 'NA') {
                    this.formGroup.get(element).enable();
                }
                else {
                    this.formGroup.get(element).disable();
                }
            });

            // Greasing Flow
            const greasingControls = this.formGroup.get('cdftXmtWhvGreasing');
            if (valvedTest == 'NA' || valvedTest == 'UTT') {
                greasingControls.get('before').disable();
                greasingControls.get('after').disable();
                const greasingValues = {
                    time0: null,
                    time1: null,
                    time2: null,
                    time3: null,
                    time4: null,
                    time5: null,
                    time6: null,
                    time7: null,
                    time8: null,
                    time9: null,
                    time10: null,
                    result: valvedTest,
                };
                greasingControls.get('before').patchValue(greasingValues);
                greasingControls.get('after').patchValue(greasingValues);
                this.formGroup.patchValue({
                    actualLeakRate: 0,
                });
            } else {
                greasingControls.get('before').enable();
                greasingControls.get('after').enable();
            }
        };

        valveBehaviour(this.formGroup.get('valvedTest').value);
        this.formGroup
            .get('valvedTest')
            .valueChanges.subscribe((valvedTest) => {
                valveBehaviour(valvedTest);
            });
    }

    onActuated(actuatorType: string | 'Pneumatic' | 'Manual' | 'Hydraulic' | null) {
        if (!actuatorType) actuatorType = 'Pneumatic';

        const openingControls = [
            'openingCdftSec',
            'openingCdftCm',
            'closingCdftSec',
            'closingCdftCm'
        ];

        const fullControls = [
            'fullyOpenCdft',
            'fullyCloseCdft'
        ];

        // Pneumatic logic (unchanged)
        openingControls.forEach(ctrl => {
            const control = this.formGroup.get(ctrl);
            if (!control) return;

            if (actuatorType !== 'Pneumatic') {
                control.reset();
                control.disable();
            } else {
                control.enable();
            }
        });

        // Manual logic (unchanged)
        fullControls.forEach(ctrl => {
            const control = this.formGroup.get(ctrl);
            if (!control) return;

            if (actuatorType !== 'Manual') {
                control.reset();
                control.disable();
            } else {
                control.enable();
            }
        });

        // ✅ NEW: Hydraulic-specific logic
        const hydraulicReturnCtrl = this.formGroup.get('hydraulicReturn');
        if (hydraulicReturnCtrl) {
            if (actuatorType === 'Hydraulic') {
                hydraulicReturnCtrl.enable();
            } else {
                hydraulicReturnCtrl.reset();
                hydraulicReturnCtrl.disable();
            }
        }
    }


    onGreasing() {
        const greasingControls = this.formGroup.get('cdftXmtWhvGreasing');

        const listenTo = [
            'cdftXmtWhvGreasing.before.time0',
            'cdftXmtWhvGreasing.before.time10',
            'cdftXmtWhvGreasing.after.time0',
            'cdftXmtWhvGreasing.after.time10',
        ];

        listenTo.forEach((row) => {
            const result = this.formGroup
                .get(row)
                .valueChanges.subscribe((value) => {
                    const type = row.includes('after') ? 'after' : 'before';
                    let result = 'UTT';
                    let actualLeakRate = null;
                    const valvedTest = this.formGroup.get('valvedTest').value;

                    if (valvedTest == 'UTT') {
                        greasingControls.get(type).patchValue({
                            result,
                        });
                        return;
                    }

                    try {
                        const greasing = greasingControls
                            .get(type)
                            .getRawValue();

                        // if (isNull(greasing.time0) || isNull(greasing.time10)) {
                        //     greasing.time0 = 0;
                        //     greasing.time10 = 0;
                        // }

                        actualLeakRate =
                            parseInt(greasing.time10) -
                            parseInt(greasing.time0);


                        if (isNaN(actualLeakRate)) {
                            result = 'UTT';
                            actualLeakRate = 0;
                        } else {
                            if (actualLeakRate < 0) result = 'UTT'
                            else if (actualLeakRate >= 0 && actualLeakRate <= 100) result = 'GOOD';
                            else if (actualLeakRate <= 200) result = 'DEGRADED';
                            else result = 'FAIL';
                        }

                        greasingControls.get(type).patchValue({
                            result,
                        });
                    } catch (e) { }

                    if (type == 'after') {
                        this.formGroup.patchValue({
                            actualLeakRate,
                        });
                    }
                });

            this.observable$.results.push(result);
        });
    }

    onJammed() {
        this.formGroup.get('jammedPosition').valueChanges.subscribe(value => {
            const control = this.formGroup.get('jammed');

            if (value) {
                control.enable()
            } else {
                control.disable()
            }
        })
    }

    onFinding() {
        this.formGroup.get('finding').valueChanges.subscribe(value => {
            const control = this.formGroup.get('othersFinding');

            if (value === 'OTHERS') {
                control.enable()
            } else {
                control.patchValue('')
                control.disable()
            }
        })
    }

    safeNumber(value: any): string | number {
        return isNaN(value) || value == null ? '' : value;
    }
}
